import {
  type DependencyMapNode,
  ReferenceAddressItemRelationType,
  type ReferenceAddress,
  type ReferenceAddressItem,
} from '../types';
import { RawGraphNode2 } from '@ardoq/graph';
import { GroupType } from '@ardoq/api-types';

type AddressReferencesArgs = {
  nodes: DependencyMapNode[];
  groupMap: Map<string, RawGraphNode2>;
  graphItemIdsByNodeId: Map<string, string>;
  getDirectReferenceNodes: (node: DependencyMapNode) => DependencyMapNode[];
};

const isGroup = (
  item: RawGraphNode2
): item is RawGraphNode2 & { type: GroupType } => Boolean(item.type);

const groupTypeToRelationType = (type: GroupType) => {
  switch (type) {
    case GroupType.WORKSPACE:
      return ReferenceAddressItemRelationType.WORKSPACE;
    case GroupType.PARENT:
    case GroupType.PARENT_ALL:
    case GroupType.CHILD:
      return ReferenceAddressItemRelationType.CHILD;
    case GroupType.REFERENCE:
      return ReferenceAddressItemRelationType.REFERENCE_GROUP;
    default:
      return ReferenceAddressItemRelationType.GROUP_MEMBER;
  }
};

type ReferenceAddressMapReducerState = {
  result: Map<string, ReferenceAddress[]>;
  currentAddress: ReferenceAddressItem[];
  groupMap: Map<string, RawGraphNode2>;
  graphItemIdsByNodeId: Map<string, string>;
};

const getNodeAddressMap = ({
  nodes,
  groupMap,
  graphItemIdsByNodeId,
  getDirectReferenceNodes,
}: AddressReferencesArgs) => {
  const nodeAddressMapReducer = (
    state: ReferenceAddressMapReducerState,
    currentNode: DependencyMapNode
  ) => {
    const nodeInfo = { name: currentNode.label, isHidden: false };
    state.result.set(currentNode.id, [
      [
        ...state.currentAddress,
        {
          nodeInfo,
          relation: ReferenceAddressItemRelationType.SELF,
          relationGroupType: null,
        },
      ],
    ]);
    const nodeReferencesAddress = [
      ...state.currentAddress,
      {
        nodeInfo,
        relation: ReferenceAddressItemRelationType.REFERENCE,
        relationGroupType: null,
      },
    ];
    const allReferenceNodes = getDirectReferenceNodes(currentNode);
    allReferenceNodes.reduce(nodeAddressMapReducer, {
      ...state,
      currentAddress: nodeReferencesAddress,
    });
    if (currentNode.children) {
      const graphItemId = state.graphItemIdsByNodeId.get(currentNode.id);
      const graphItem = graphItemId && state.groupMap.get(graphItemId);
      if (graphItem && isGroup(graphItem)) {
        const relation = groupTypeToRelationType(graphItem.type);
        const relationGroupType = graphItem.type;
        const nodeChildrenAddress = [
          ...state.currentAddress,
          {
            nodeInfo,
            relation,
            relationGroupType,
          },
        ];
        currentNode.children.reduce(nodeAddressMapReducer, {
          ...state,
          currentAddress: nodeChildrenAddress,
        });
      }
    }
    return state;
  };

  return nodes.reduce(nodeAddressMapReducer, {
    currentAddress: [],
    graphItemIdsByNodeId,
    groupMap,
    result: new Map(),
  }).result;
};

export { getNodeAddressMap };
